import React from 'react'
import moment from 'moment'
import {Link} from 'react-router-dom'
import {Badge, Table} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {ContactPolicyGroup} from './contactProfileTypes'
import {parseId} from '../../../../helpers'
import {getDocumentVariant, getPaymentVariant, getBadgeTextColor} from '../../../../helpers/consts'
import {UserAvatar} from '../../../../components/UserAvatar'
import {AgentInfoTooltip} from '../../../shared/AgentInfoTooltip'
import {BadgeStatePolicy} from '../../../onePolicy/components/BadgeStatePolicy'
import {getColorByStatus, getTitleByStatus} from '../../../shared/utils'
import {AllPolicyStatusesData} from '../../../../types/policies'
import {PolicySummary} from '../../../../types/contactsApi'

const sp = ' '
const COLUMN_COUNT = 5

/** `UserAvatar` / `AgentInfoTooltip` esperan strings; la API los devuelve nullables. */
const agentUser = (user: PolicySummary['user']) =>
  user
    ? {
        ...user,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      }
    : null

interface ContactPoliciesTabProps {
  groups: ContactPolicyGroup[]
  policiesStatuses: AllPolicyStatusesData | undefined
  onNavigate?: () => void
}

export const ContactPoliciesTab: React.FC<ContactPoliciesTabProps> = ({
  groups,
  policiesStatuses,
  onNavigate,
}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const statusEdges = policiesStatuses?.allPolicyStatuses?.edges ?? []
  const total = groups.reduce((acc, group) => acc + group.entries.length, 0)

  return (
    <div className='policy-form-tabs-container'>
      <div className='kt-heading kt-heading--md custom-border-brand d-flex align-items-center justify-content-between'>
        <span>
          <FormattedMessage id='contacts.profile.policies.title' defaultMessage='Policies' />
        </span>
        <Badge bg='secondary' text='dark'>
          <FormattedMessage
            id='contacts.profile.policies.total'
            defaultMessage='{count, plural, one {# policy} other {# policies}}'
            values={{count: total}}
          />
        </Badge>
      </div>

      {groups.length === 0 ? (
        <div className='text-muted py-4 text-center'>
          <FormattedMessage
            id='contacts.profile.policies.empty'
            defaultMessage='No policies found for this contact.'
          />
        </div>
      ) : (
        // Una sola tabla para todos los grupos: cabecera única y el tipo de póliza como
        // fila separadora, para que las columnas queden alineadas entre grupos.
        <Table responsive size='sm' className='mb-0 align-middle'>
          <thead>
            <tr>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.agentOwner'
                  defaultMessage='Agent Owner'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.policy'
                  defaultMessage='Policy'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.status'
                  defaultMessage='Status'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.agentOfRecord'
                  defaultMessage='Agent of Record'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.role'
                  defaultMessage='Role/Relationship'
                />
              </th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.type}>
              <tr>
                <td colSpan={COLUMN_COUNT} className='bg-light'>
                  <div className='d-flex align-items-center gap-2'>
                    <Badge bg='primary' className='text-white fs-7'>
                      {group.type}
                    </Badge>
                    <span className='text-muted' style={{fontSize: '0.9rem'}}>
                      <FormattedMessage
                        id='contacts.profile.policies.groupCount'
                        defaultMessage='{count, plural, one {# policy} other {# policies}}'
                        values={{count: group.entries.length}}
                      />
                    </span>
                  </div>
                </td>
              </tr>
              {group.entries.map(({policy, related}) => {
                const applicantsCount =
                  policy.applicants && policy.applicants > 0
                    ? policy.applicants
                    : policy.members ?? 0
                return (
                  <tr key={policy.id}>
                    <td>
                      <UserAvatar
                        user={agentUser(policy.user)}
                        tooltipId={`contact-policy-agent-${policy.id}`}
                        size={40}
                      />
                      <AgentInfoTooltip
                        id={`contact-policy-agent-${policy.id}`}
                        user={agentUser(policy.user)}
                      />
                    </td>
                    <td className='limit-width'>
                      <div className='info-policy-name'>
                        <Link
                          className='text-primary text-link fw-bold'
                          to={`/one-policy/${parseId(policy.id)}`}
                          onClick={onNavigate}
                        >
                          {policy.carrier?.name || policy.policyName || na}
                        </Link>
                        <p className='m-0 p-0' style={{fontSize: '0.95rem'}}>
                          <FormattedMessage
                            id='contacts.profile.policies.effective'
                            defaultMessage='Effective:'
                          />
                          {sp}
                          {policy.effectiveDate
                            ? moment(policy.effectiveDate).format('MMM DD, YYYY')
                            : na}
                        </p>
                        <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
                          <Badge
                            bg='primary'
                            className='d-flex align-items-center gap-1 text-white'
                            title={intl.formatMessage(
                              {
                                id: 'contacts.profile.policies.applicants',
                                defaultMessage:
                                  '{count, plural, one {# Applicant} other {# Applicants}}',
                              },
                              {count: applicantsCount}
                            )}
                          >
                            <i className='bi bi-people-fill' />
                            {applicantsCount}
                          </Badge>
                          {policy.memberId && (
                            <span className='m-0 p-0' style={{fontSize: '0.9rem'}}>
                              <FormattedMessage
                                id='contacts.profile.policies.memberId'
                                defaultMessage='ID:'
                              />
                              {sp}
                              {policy.memberId}
                            </span>
                          )}
                          {policy.hasAutoPolicy === true && (
                            <Badge
                              bg='danger'
                              className='d-flex align-items-center text-white'
                              title={intl.formatMessage({
                                id: 'contacts.profile.policies.hasAuto',
                                defaultMessage: 'Has auto policy',
                              })}
                            >
                              <i
                                className='bi bi-car-front-fill'
                                style={{color: 'var(--bs-white)'}}
                              />
                            </Badge>
                          )}
                          {policy.hasHomePolicy === true && (
                            <Badge
                              bg='success'
                              className='d-flex align-items-center text-white'
                              title={intl.formatMessage({
                                id: 'contacts.profile.policies.hasHome',
                                defaultMessage: 'Has home policy',
                              })}
                            >
                              <i className='bi bi-house-fill' style={{color: 'var(--bs-white)'}} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {policy.status ? (
                        <div className='mb-1'>
                          <BadgeStatePolicy
                            status={getTitleByStatus(statusEdges, policy.status)}
                            color={getColorByStatus(statusEdges, policy.status)}
                          />
                        </div>
                      ) : (
                        <div className='text-muted mb-1'>{na}</div>
                      )}
                      {policy.stateDocs ? (
                        <div className='mb-1'>
                          <span className='text-muted me-1'>
                            <FormattedMessage
                              id='contacts.profile.policies.documents'
                              defaultMessage='Documents:'
                            />
                          </span>
                          <Badge className='badge-sm' bg={getDocumentVariant(policy.stateDocs)} text={getBadgeTextColor(getDocumentVariant(policy.stateDocs))}>
                            {policy.stateDocs}
                          </Badge>
                        </div>
                      ) : null}
                      {policy.paymentStatus ? (
                        <div>
                          <span className='text-muted me-1'>
                            <FormattedMessage
                              id='contacts.profile.policies.payment'
                              defaultMessage='Payment:'
                            />
                          </span>
                          <Badge
                            className='badge-sm'
                            bg={getPaymentVariant(policy.paymentStatus)}
                            text={
                              getPaymentVariant(policy.paymentStatus) === 'danger'
                                ? 'white'
                                : undefined
                            }
                          >
                            {policy.paymentStatus}
                          </Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>{policy.agentOfRecord || na}</td>
                    <td>
                      <div className='fw-bold'>{related?.rol || na}</div>
                      {related?.rol && related.rol !== 'HOLDER' ? (
                        <Badge bg='dark' text='white' className='badge-sm'>
                          {related.relationship || na}
                        </Badge>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </Table>
      )}
    </div>
  )
}
